<template>
  <div class="settings-panel">
    <!-- Header with search and actions -->
    <header class="settings-header">
      <div class="header-content">
        <h1 class="settings-title">
          <Icon name="settings-gear" :size="20" />
          {{ title }}
        </h1>
        <div class="header-actions">
          <Tooltip content="Toggle theme">
            <Button 
              variant="icon" 
              :icon="isDarkTheme ? 'color-mode' : 'symbol-color'"
              @click="toggleTheme"
            />
          </Tooltip>
        </div>
      </div>
      
      <div class="settings-search">
        <SearchBox
          v-model="searchQuery"
          placeholder="Search settings..."
          :result-count="filteredSettings.length"
          :has-filter="hasActiveFilters"
          clearable
          @search="onSearch"
          @clear="onClearSearch"
          @filter-click="toggleFilters"
        />
      </div>
      
      <SettingsTargetsWidget
        v-model="activeScope"
        @action="onScopeAction"
        class="settings-scope"
      />
    </header>

    <!-- Main content with sidebar and settings -->
    <div class="settings-content">
      <SplitView
        v-model:collapsed="sidebarCollapsed"
        v-model:primarySize="sidebarWidth"
        orientation="horizontal"
        :min-primary-size="200"
        :max-primary-size="400"
        class="settings-split"
      >
        <template #primary>
          <aside class="settings-sidebar">
            <nav class="settings-nav">
              <h3>Categories</h3>
              <TreeView
                :nodes="categoryTree"
                :selected-id="selectedCategory"
                :expanded-ids="expandedCategories"
                @select="onCategorySelect"
                @expand="onCategoryExpand"
                @collapse="onCategoryCollapse"
              />
            </nav>
            
            <div class="sidebar-actions">
              <Button 
                size="small" 
                variant="secondary"
                @click="resetSettings"
              >
                Reset to Defaults
              </Button>
            </div>
          </aside>
        </template>

        <template #secondary>
          <main class="settings-main">
            <div v-if="filteredSettings.length === 0" class="settings-empty">
              <Icon name="search" :size="48" />
              <h3>No settings found</h3>
              <p>Try adjusting your search or filters.</p>
            </div>
            
            <div v-else class="settings-groups">
              <SettingsGroup
                v-for="group in settingsGroups"
                :key="group.id"
                :group="group"
                :scope="activeScope"
                @update="onSettingUpdate"
              />
            </div>
          </main>
        </template>
      </SplitView>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import {
  Button, Icon, Tooltip, TreeView
} from '@/components/atoms';
import { SearchBox, SplitView, SettingsTargetsWidget } from '@/components/molecules';
import SettingsGroup from './SettingsGroup.vue';
import type { TreeViewNode } from '@/components/atoms/TreeView.vue';

export interface SettingsItem {
  id: string;
  key: string;
  label: string;
  description?: string;
  type: 'boolean' | 'string' | 'number' | 'select' | 'enum';
  value: any;
  defaultValue: any;
  options?: Array<{ label: string; value: any; description?: string }>;
  category: string;
  subcategory?: string;
  tags?: string[];
  scope?: 'user' | 'workspace' | 'both';
  deprecated?: boolean;
  experimental?: boolean;
}

export interface SettingsGroup {
  id: string;
  title: string;
  description?: string;
  items: SettingsItem[];
}

export interface SettingsPanelProps {
  title?: string;
  settings: SettingsItem[];
  isDarkTheme?: boolean;
}

const props = withDefaults(defineProps<SettingsPanelProps>(), {
  title: 'Settings',
  isDarkTheme: false,
});

const emit = defineEmits<{
  'update:setting': [key: string, value: any, scope: 'user' | 'workspace'];
  'reset-settings': [];
  'toggle-theme': [];
}>();

// State
const searchQuery = ref('');
const activeScope = ref<'user' | 'workspace'>('user');
const selectedCategory = ref('');
const expandedCategories = ref<string[]>(['general']);
const sidebarCollapsed = ref(false);
const sidebarWidth = ref(280);
const hasActiveFilters = ref(false);

// Generate category tree from settings
const categoryTree = computed((): TreeViewNode[] => {
  const categories = new Map<string, Set<string>>();
  
  // Collect categories and subcategories
  props.settings.forEach(setting => {
    if (!categories.has(setting.category)) {
      categories.set(setting.category, new Set());
    }
    if (setting.subcategory) {
      categories.get(setting.category)?.add(setting.subcategory);
    }
  });
  
  // Build tree
  return Array.from(categories.entries()).map(([category, subcategories]) => ({
    id: category,
    label: formatCategoryLabel(category),
    icon: getCategoryIcon(category),
    children: Array.from(subcategories).map(sub => ({
      id: `${category}.${sub}`,
      label: formatCategoryLabel(sub),
      icon: 'circle-filled',
    }))
  }));
});

// Filter settings based on search and category
const filteredSettings = computed(() => {
  let filtered = props.settings;
  
  // Apply search filter
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(setting => 
      setting.label.toLowerCase().includes(query) ||
      setting.description?.toLowerCase().includes(query) ||
      setting.key.toLowerCase().includes(query) ||
      setting.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  }
  
  // Apply category filter
  if (selectedCategory.value) {
    const [category, subcategory] = selectedCategory.value.split('.');
    filtered = filtered.filter(setting => {
      if (subcategory) {
        return setting.category === category && setting.subcategory === subcategory;
      }
      return setting.category === category;
    });
  }
  
  return filtered;
});

// Group filtered settings by category
const settingsGroups = computed((): SettingsGroup[] => {
  const groups = new Map<string, SettingsItem[]>();
  
  filteredSettings.value.forEach(setting => {
    const groupKey = setting.subcategory 
      ? `${setting.category}.${setting.subcategory}`
      : setting.category;
    
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)?.push(setting);
  });
  
  return Array.from(groups.entries()).map(([key, items]) => ({
    id: key,
    title: formatCategoryLabel(key.split('.').pop() || key),
    items: items.sort((a, b) => a.label.localeCompare(b.label))
  }));
});

// Event handlers
const onSearch = (query: string) => {
  searchQuery.value = query;
};

const onClearSearch = () => {
  searchQuery.value = '';
};

const toggleFilters = () => {
  hasActiveFilters.value = !hasActiveFilters.value;
};

const onCategorySelect = (nodeId: string) => {
  selectedCategory.value = nodeId;
};

const onCategoryExpand = (nodeId: string) => {
  if (!expandedCategories.value.includes(nodeId)) {
    expandedCategories.value.push(nodeId);
  }
};

const onCategoryCollapse = (nodeId: string) => {
  const index = expandedCategories.value.indexOf(nodeId);
  if (index > -1) {
    expandedCategories.value.splice(index, 1);
  }
};

const onScopeAction = (actionKey: string, scope: 'user' | 'workspace') => {
  console.log(`${actionKey} action for ${scope} scope`);
};

const onSettingUpdate = (key: string, value: any) => {
  emit('update:setting', key, value, activeScope.value);
};

const resetSettings = () => {
  emit('reset-settings');
};

const toggleTheme = () => {
  emit('toggle-theme');
};

// Helper functions
const formatCategoryLabel = (category: string): string => {
  return category
    .split(/[-_]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getCategoryIcon = (category: string): string => {
  const iconMap: Record<string, string> = {
    general: 'settings-gear',
    editor: 'edit',
    workbench: 'window',
    extensions: 'extensions',
    security: 'shield',
    privacy: 'eye',
    accessibility: 'accessibility',
    terminal: 'terminal',
    debug: 'debug-alt',
    git: 'source-control',
    search: 'search',
  };
  
  return iconMap[category.toLowerCase()] || 'circle-filled';
};
</script>

<style scoped lang="scss">
@use '@/styles/mixins';

.settings-panel {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--vscode-background);
  color: var(--vscode-foreground);
}

.settings-header {
  border-bottom: 1px solid var(--vscode-settings-headerBorder);
  padding: var(--spacing-lg);
  background-color: var(--vscode-backgroundSecondary);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-lg);
}

.settings-title {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.settings-search {
  margin-bottom: var(--spacing-lg);
}

.settings-scope {
  max-width: 500px;
}

.settings-content {
  flex: 1;
  overflow: hidden;
}

.settings-split {
  height: 100%;
}

.settings-sidebar {
  height: 100%;
  background-color: var(--vscode-backgroundSecondary);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.settings-nav {
  flex: 1;
  padding: var(--spacing-lg);
  overflow-y: auto;
  
  h3 {
    margin: 0 0 var(--spacing-md) 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--vscode-foreground);
    opacity: 0.8;
  }
}

.sidebar-actions {
  padding: var(--spacing-lg);
  border-top: 1px solid var(--vscode-border);
}

.settings-main {
  height: 100%;
  padding: var(--spacing-lg);
  overflow-y: auto;
}

.settings-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  text-align: center;
  color: var(--vscode-foreground);
  opacity: 0.6;
  
  /* Removed legacy .codicon styling after icon migration */
  
  h3 {
    margin: 0 0 var(--spacing-sm) 0;
    font-size: 1.1rem;
    font-weight: 500;
  }
  
  p {
    margin: 0;
    font-size: 14px;
  }
}

.settings-groups {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxl);
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: var(--spacing-lg);
    align-items: flex-start;
  }
  
  .settings-scope {
    max-width: none;
    width: 100%;
  }
}
</style>
